import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type {
  ChartData,
  ChartStatus,
  Subpage,
} from 'types/Meteorology/Fields/TrackingForecast/DetailedView';
import { CHART_TYPE_LINE, CHART_TYPE_HEATMAP, type ChartType } from 'types/Meteorology/TrackingForecast';

export interface MeteorologyFieldsTrackingForecastControlPanelState {
  subpage: Subpage;
  chartType: ChartType;
  groupId: number | undefined;
  multiGroupIds: number[];
  locationId: number | undefined;
  multilocationIds: number[];
  timeFrequency: string | undefined;
  modelId: number | undefined;
  multimodelIds: number[];
  forecastDate: string;
  variable: string | undefined;
  includeHistoricYears: boolean;
  includeObserved: boolean;
  rounds: string[];
  members: number[];
  // Sinaliza que a abertura está preenchendo os defaults e vai auto-consultar
  // (#1822). Fica ligada durante toda a cascata (frequências → modelos → variáveis
  // → data-previsão) e só desliga quando a consulta final dispara — o gráfico usa
  // isso para mostrar o spinner o tempo todo, não só na requisição final.
  autoQuerying: boolean;
  chartData: ChartData;
}

const initialStatus: ChartStatus = {
  data: undefined, error: undefined, isFetching: false,
};
const initialChartData: ChartData = {
  locations: {
    [CHART_TYPE_LINE]: { ...initialStatus },
    [CHART_TYPE_HEATMAP]: { ...initialStatus },
  },
  members: {
    [CHART_TYPE_LINE]: { ...initialStatus },
    [CHART_TYPE_HEATMAP]: { ...initialStatus },
  },
  models: {
    [CHART_TYPE_LINE]: { ...initialStatus },
    [CHART_TYPE_HEATMAP]: { ...initialStatus },
  },
  rounds: {
    [CHART_TYPE_LINE]: { ...initialStatus },
    [CHART_TYPE_HEATMAP]: { ...initialStatus },
  },
};
const initialState: MeteorologyFieldsTrackingForecastControlPanelState = {
  subpage: 'locations',
  chartType: CHART_TYPE_LINE,
  groupId: undefined,
  multiGroupIds: [],
  locationId: undefined,
  multilocationIds: [],
  timeFrequency: undefined,
  modelId: undefined,
  multimodelIds: [],
  forecastDate: '',
  variable: undefined,
  includeHistoricYears: false,
  includeObserved: false,
  rounds: [],
  members: [],
  autoQuerying: false,
  chartData: initialChartData,
};

export const meteorologyFieldsTrackingForecastControlPanelSlice = createSlice({
  name: 'meteorologyFieldsTrackingForecastControlPanel',
  initialState,
  reducers: {
    resetCP: (state) => {
      Object.assign(state, {
        ...state,
        groupId: undefined,
        multiGroupIds: [],
        locationId: undefined,
        multilocationIds: [],
        timeFrequency: undefined,
        modelId: undefined,
        multimodelIds: [],
        forecastDate: '',
        variable: undefined,
        includeHistoricYears: false,
        includeObserved: false,
        rounds: [],
        members: [],
        autoQuerying: false,
        chartData: initialChartData,
      });
    },
    setSubpage: (state, action: PayloadAction<Subpage>) => {
      state.subpage = action.payload;
    },
    setChartType: (state, action: PayloadAction<ChartType>) => {
      state.chartType = action.payload;
    },
    setGroupId: (state, action: PayloadAction<number | undefined>) => {
      state.groupId = action.payload;
    },
    setMultiGroupIds: (state, action: PayloadAction<number[]>) => {
      state.multiGroupIds = action.payload;
    },
    setLocationId: (state, action: PayloadAction<number | undefined>) => {
      state.locationId = action.payload;
    },
    setMultiLocationIds: (state, action: PayloadAction<number[]>) => {
      state.multilocationIds = action.payload;
    },
    setTimeFrequency: (state, action: PayloadAction<string | undefined>) => {
      state.timeFrequency = action.payload;
    },
    setModelId: (state, action: PayloadAction<number | undefined>) => {
      state.modelId = action.payload;
    },
    setMultiModelIds: (state, action: PayloadAction<number[]>) => {
      state.multimodelIds = action.payload;
    },
    setForecastDate: (state, action: PayloadAction<string>) => {
      state.forecastDate = action.payload;
    },
    setVariable: (state, action: PayloadAction<string>) => {
      state.variable = action.payload;
    },
    setIncludeHistoricYears: (state, action: PayloadAction<boolean>) => {
      state.includeHistoricYears = action.payload;
    },
    setIncludeObserved: (state, action: PayloadAction<boolean>) => {
      state.includeObserved = action.payload;
    },
    setRounds: (state, action: PayloadAction<string[]>) => {
      state.rounds = action.payload;
    },
    setMembers: (state, action: PayloadAction<number[]>) => {
      state.members = action.payload;
    },
    setAutoQuerying: (state, action: PayloadAction<boolean>) => {
      state.autoQuerying = action.payload;
    },
    setChartData: (
      state,
      action: PayloadAction<[subpage: Subpage, chartType: ChartType, status: ChartStatus]>,
    ) => {
      const [subpage, chartType, status] = action.payload;
      state.chartData[subpage][chartType] = status;
    },
  },
});

export const selectCPMFTrackingForecastSubpage = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.subpage;
export const selectCPMFTrackingForecastChartType = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.chartType;
export const selectCPMFTrackingForecastGroupId = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.groupId;
export const selectCPMFTrackingForecastMultiGroupIds = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.multiGroupIds;
export const selectCPMFTrackingForecastLocationId = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.locationId;
export const selectCPMFTrackingForecastMultiLocationIds = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.multilocationIds;
export const selectCPMFTrackingForecastTimeFrequency = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.timeFrequency;
export const selectCPMFTrackingForecastModelId = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.modelId;
export const selectCPMFTrackingForecastMultiModelIds = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.multimodelIds;
export const selectCPMFTrackingForecastForecastDate = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.forecastDate;
export const selectCPMFTrackingForecastVariable = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.variable;
export const selectCPMFTrackingForecastIncludeHistoricYears = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.includeHistoricYears;
export const selectCPMFTrackingForecastIncludeObserved = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.includeObserved;
export const selectCPMFTrackingForecastRounds = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.rounds;
export const selectCPMFTrackingForecastMembers = (state: any) => state
  .meteorologyFieldsTrackingForecastControlPanel.members;
export const selectCPMFTrackingForecastChartData = (state: any) => (
  state.meteorologyFieldsTrackingForecastControlPanel.chartData
);
export const selectCPMFTrackingForecastAutoQuerying = (state: any) => (
  state.meteorologyFieldsTrackingForecastControlPanel.autoQuerying
);

export const {
  resetCP,
  setSubpage,
  setChartType,
  setGroupId,
  setMultiGroupIds,
  setLocationId,
  setMultiLocationIds,
  setTimeFrequency,
  setModelId,
  setMultiModelIds,
  setForecastDate,
  setVariable,
  setIncludeHistoricYears,
  setIncludeObserved,
  setRounds,
  setMembers,
  setAutoQuerying,
  setChartData,
} = meteorologyFieldsTrackingForecastControlPanelSlice.actions;

export default meteorologyFieldsTrackingForecastControlPanelSlice.reducer;
